import SearchableSelect from './search/SearchableSelect';
import CollectionSearch from './search/CollectionSearch';
import { filterRecords } from '../utils/search';
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Alert,
  Divider,
  Stack,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { getCategoryTotals, transferCategoryBalance } from '../services/api';

function CategoryTransferModal({ open, onClose, account, categories, onTransferComplete }) {
  const [sourceCategory, setSourceCategory] = useState('');
  const [sourceCategoryBalance, setSourceCategoryBalance] = useState(0);
  const [targetCategories, setTargetCategories] = useState([]);
  const [targetSearch, setTargetSearch] = useState('');
  const visibleTargets = filterRecords(targetCategories, targetSearch, category => [category.name], { rank: true });
  const [allocations, setAllocations] = useState({});
  const [deprecateCategory, setDeprecateCategory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Calculate totals
  const allocatedTotal = Object.values(allocations).reduce((sum, amount) => sum + parseFloat(amount || 0), 0);
  const remaining = sourceCategoryBalance - allocatedTotal;
  
  // Validation logic for both modes
  const canTransfer = sourceCategory && allocatedTotal > 0 && (
    deprecateCategory 
      ? Math.abs(remaining) < 0.01  // Must equal zero for deprecation
      : allocatedTotal <= sourceCategoryBalance // Partial allowed
  );

  // Get available target categories (exclude selected source category)
  useEffect(() => {
    if (sourceCategory) {
      const availableTargets = categories.filter(cat => 
        cat.id !== parseInt(sourceCategory) && !cat.is_deleted
      );
      setTargetCategories(availableTargets);
    } else {
      setTargetCategories([]);
    }
    setAllocations({});
  }, [sourceCategory, categories]);

  // Fetch source category balance when selected
  useEffect(() => {
    if (sourceCategory) {
      fetchSourceCategoryBalance();
    } else {
      setSourceCategoryBalance(0);
    }
  }, [sourceCategory]);

  const fetchSourceCategoryBalance = async () => {
    try {
      setLoading(true);
      const response = await getCategoryTotals(account.id);
      const categoryTotal = response.data.find(
        cat => cat.category_name === categories.find(c => c.id === parseInt(sourceCategory))?.name
      );
      setSourceCategoryBalance(parseFloat(categoryTotal?.total_amount || 0));
    } catch (err) {
      console.error('Failed to fetch category balance:', err);
      setError('Failed to load category balance');
    } finally {
      setLoading(false);
    }
  };

  const handleAllocationChange = (categoryId, value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < 0) return; // Prevent negative values
    
    // Prevent over-allocation in partial mode
    if (!deprecateCategory) {
      const otherAllocations = Object.entries(allocations)
        .filter(([id]) => id !== categoryId.toString())
        .reduce((sum, [, amount]) => sum + parseFloat(amount || 0), 0);
      
      if (otherAllocations + numValue > sourceCategoryBalance) {
        return; // Don't allow over-allocation
      }
    }
    
    setAllocations(prev => ({
      ...prev,
      [categoryId]: numValue
    }));
  };

  const handleTransfer = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Call category transfer API endpoint
      const response = await transferCategoryBalance(
        parseInt(sourceCategory),
        allocations,
        deprecateCategory
      );
      
      // Show success message
      const sourceCategoryName = response.data.source_category.name;
      const allocationsText = response.data.target_allocations
        .map(alloc => `  • ${alloc.category_name}: $${alloc.amount}`)
        .join('\n');

      const modeText = response.data.source_category.deprecated 
        ? `"${sourceCategoryName}" has been deprecated and archived.`
        : `"${sourceCategoryName}" retains $${response.data.remaining_balance}.`;

      alert(`Category transfer completed successfully!\n\nTransferred from "${sourceCategoryName}":\n${allocationsText}\n\n${modeText}`);
      
      // Refresh data and close modal
      onTransferComplete?.();
      handleClose();
      
    } catch (err) {
      console.error('Transfer failed:', err);
      const errorMsg = err.response?.data?.msg || 'Transfer failed. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSourceCategory('');
    setSourceCategoryBalance(0);
    setTargetCategories([]);
    setAllocations({});
    setDeprecateCategory(false);
    setError('');
    onClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          🔄 Category Transfer Tool
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Transfer balance between categories within {account?.name}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Source Category Selection */}
        <Box sx={{ mb: 4 }}>
          <FormControl fullWidth sx={{ minWidth: 300 }}>
            <InputLabel>Source Category (Transfer FROM)</InputLabel>
            <SearchableSelect
              value={sourceCategory}
              onChange={(e) => setSourceCategory(e.target.value)}
              label="Source Category (Transfer FROM)"
              sx={{ minWidth: 300 }}
            >
              {categories.filter(cat => !cat.is_deleted).map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </SearchableSelect>
          </FormControl>
        </Box>

        {/* Deprecate Category Checkbox */}
        {sourceCategory && (
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={deprecateCategory}
                  onChange={(e) => {
                    setDeprecateCategory(e.target.checked);
                    if (e.target.checked) {
                      // Clear allocations when switching to deprecate mode
                      setAllocations({});
                    }
                  }}
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    Deprecate category after transfer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {deprecateCategory 
                      ? "Must transfer entire balance. Category will be archived." 
                      : "Transfer any amount. Category remains active with remaining balance."}
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}

        {sourceCategory && (
          <>
            <Grid container spacing={3}>
              {/* Source Category Balance - READ ONLY */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    📊 {categories.find(c => c.id === parseInt(sourceCategory))?.name} Balance
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    (Source category balance)
                  </Typography>
                  
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: '#e3f2fd',
                      borderRadius: 1,
                      border: '2px solid #1976d2',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="h4" color="primary" fontWeight="bold">
                      {formatCurrency(sourceCategoryBalance)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Target Categories - INTERACTIVE */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: '#f0f8f0' }}>
                  <Typography variant="h6" gutterBottom color="success.main">
                    🎯 Allocate to Other Categories
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    (Enter allocation amounts)
                  </Typography>
                  
                  <Stack spacing={2} sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    <CollectionSearch label="Search transfer categories" value={targetSearch} onChange={setTargetSearch}
                      total={targetCategories.length} shown={visibleTargets.length} />
                    {targetSearch && !visibleTargets.length && <Typography>No matching categories.</Typography>}
                    {targetCategories.length > 0 ? (
                      visibleTargets.map((category) => (
                        <Box key={category.id}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {category.name}
                          </Typography>
                          <TextField
                            fullWidth
                            type="number"
                            inputProps={{
                              step: "0.01",
                              min: "0",
                              max: deprecateCategory ? sourceCategoryBalance : undefined
                            }}
                            value={allocations[category.id] || ''}
                            onChange={(e) => handleAllocationChange(category.id, e.target.value)}
                            placeholder="0.00"
                            size="small"
                          />
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No other categories available for transfer.
                      </Typography>
                    )}
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box
                      sx={{
                        p: 1.5,
                        backgroundColor: '#e8f5e8',
                        borderRadius: 1,
                        border: '2px solid #4caf50'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">
                          ALLOCATED:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(allocatedTotal)}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            {/* Transfer Status */}
            <Box sx={{ mt: 3 }}>
              <Paper sx={{ 
                p: 2, 
                backgroundColor: canTransfer ? '#e8f5e8' : '#fff3e0',
                border: `2px solid ${canTransfer ? '#4caf50' : '#ff9800'}`
              }}>
                <Typography variant="h6" gutterBottom>
                  💰 Transfer Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2">Source Balance:</Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(sourceCategoryBalance)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2">Allocated:</Typography>
                    <Typography variant="h6" color="success.main">
                      {formatCurrency(allocatedTotal)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2">
                      {deprecateCategory ? 'REMAINING (must be $0):' : 'REMAINING IN SOURCE:'}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      fontWeight="bold"
                      color={
                        deprecateCategory 
                          ? (Math.abs(remaining) < 0.01 ? 'success.main' : 'error.main')
                          : 'info.main'
                      }
                    >
                      {formatCurrency(remaining)}
                    </Typography>
                  </Grid>
                </Grid>
                
                {/* Status Messages */}
                {canTransfer && deprecateCategory && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    ✅ Ready to transfer! Entire balance allocated. Category will be deprecated.
                  </Alert>
                )}
                
                {canTransfer && !deprecateCategory && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    ✅ Ready to transfer! {formatCurrency(allocatedTotal)} will be transferred. Category keeps {formatCurrency(remaining)}.
                  </Alert>
                )}
                
                {deprecateCategory && remaining > 0.01 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    ⚠️ Must allocate remaining {formatCurrency(remaining)} to deprecate category.
                  </Alert>
                )}
                
                {deprecateCategory && remaining < -0.01 && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    ❌ Over-allocated by {formatCurrency(Math.abs(remaining))}. Reduce allocations.
                  </Alert>
                )}
                
                {!sourceCategory && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    ℹ️ Select a source category to begin transfer.
                  </Alert>
                )}
                
                {allocatedTotal === 0 && sourceCategory && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    ℹ️ Enter allocation amounts to target categories.
                  </Alert>
                )}
              </Paper>
            </Box>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleTransfer}
          disabled={!canTransfer || loading}
          color="primary"
          size="large"
        >
          {loading ? 'Processing...' : `Transfer ${deprecateCategory ? '& Deprecate' : 'Category Balance'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CategoryTransferModal;
